// ── CoffeeRoulette — Feature N (feature_coffee_roulette) ─────────────────────
// Surprise weekly coffee match within a hive group. Renders a card with status:
//   - waiting: cancel button + chosen slots
//   - matched: shows matched_with hint, link to messages
//   - none: opens a modal to pick 1-3 weekend slots
// The actual matching runs server-side via match_coffee_roulette() (see
// migration 037 + cron in /reminders Edge Function).

import { useEffect, useState } from 'react';
import { useApp } from '../contexts/AppContext';
import {
  joinCoffeeRoulette, leaveCoffeeRoulette, loadCoffeeStatus,
} from '../utils/featurePackA';

function nextWeekendSlots() {
  // Generate 4 upcoming weekend dates (Sat + Sun for the next two weeks)
  const out = [];
  const now = new Date();
  for (let i = 1; i <= 14 && out.length < 4; i++) {
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    const day = d.getDay();
    if (day === 0 || day === 6) {
      out.push(d.toISOString().slice(0, 10));
    }
  }
  return out;
}

function formatSlot(iso, lang) {
  try {
    const d = new Date(iso + 'T12:00:00Z');
    return d.toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch { return iso; }
}

export default function CoffeeRoulette({ groupId, groupName, bh }) {
  const { features, currentUser, activeNetwork, lang } = useApp();
  const [status, setStatus]     = useState(null);   // null | row
  const [showPicker, setShowPicker] = useState(false);
  const [slots, setSlots]       = useState([]);
  const [busy, setBusy]         = useState(false);

  const fr = lang === 'fr';
  const enabled = features?.feature_coffee_roulette === true;

  useEffect(() => {
    if (!enabled || !currentUser?.id || !groupId) return;
    let alive = true;
    loadCoffeeStatus({ userId: currentUser.id, networkId: activeNetwork, hiveGroup: groupId }).then(s => {
      if (alive) setStatus(s && s.status !== 'cancelled' ? s : null);
    });
    return () => { alive = false; };
  }, [enabled, currentUser?.id, activeNetwork, groupId]);

  if (!enabled || !groupId) return null;

  const handleJoin = async () => {
    if (!slots.length) return;
    setBusy(true);
    const { error } = await joinCoffeeRoulette({
      userId: currentUser.id, networkId: activeNetwork, hiveGroup: groupId, slotDates: slots,
    });
    setBusy(false);
    if (!error) {
      setShowPicker(false);
      setStatus({ status: 'waiting', slot_dates: slots, hive_group: groupId });
    }
  };

  const handleLeave = async () => {
    setBusy(true);
    await leaveCoffeeRoulette({ userId: currentUser.id, networkId: activeNetwork, hiveGroup: groupId });
    setBusy(false);
    setStatus(null);
  };

  const cardStyle = {
    background: bh?.card || 'rgba(201,168,76,0.07)',
    border: `1px solid ${bh?.border || 'rgba(201,168,76,0.18)'}`,
    borderRadius: 10, padding: '12px 14px', margin: '12px 16px',
    fontFamily: 'Outfit',
  };

  // Matched state
  if (status?.status === 'matched') {
    return (
      <div style={{ ...cardStyle, background: 'rgba(78,201,148,0.06)', border: '1px solid rgba(78,201,148,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 24 }}>☕</div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: bh?.text || '#F2E8D0' }}>
              {fr ? 'Café surprise — match trouvé !' : 'Coffee Roulette — paired!'}
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 11, color: bh?.sub || '#8A7A5A' }}>
              {fr ? 'Ouvre Messages pour saluer.' : 'Open Messages to greet them.'}
            </p>
          </div>
          <button onClick={() => { import('../utils/navigate').then(({ navigate }) => navigate('messages')); }} style={{
            padding: '6px 12px', background: '#C9A84C', border: 'none', borderRadius: 6,
            color: '#0A0A0A', fontSize: 11, fontWeight: 700, cursor: 'pointer',
          }}>
            {fr ? 'Saluer →' : 'Say hi →'}
          </button>
        </div>
      </div>
    );
  }

  // Waiting state
  if (status?.status === 'waiting') {
    const list = Array.isArray(status.slot_dates) ? status.slot_dates : [];
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 22 }}>☕</div>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: bh?.text || '#F2E8D0' }}>
              {fr ? `Café surprise · ${groupName || 'Hive'}` : `Coffee Roulette · ${groupName || 'Hive'}`}
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 11, color: bh?.sub || '#8A7A5A' }}>
              {fr ? 'Tu attends un match' : 'Waiting for a match'}
              {list.length ? ` · ${list.map(s => formatSlot(s, lang)).join(', ')}` : ''}
            </p>
          </div>
          <button onClick={handleLeave} disabled={busy} style={{
            padding: '5px 10px', background: 'transparent',
            border: `1px solid ${bh?.border || 'rgba(201,168,76,0.18)'}`,
            borderRadius: 6, color: bh?.sub || '#8A7A5A', fontSize: 10, cursor: 'pointer',
          }}>
            {fr ? 'Annuler' : 'Cancel'}
          </button>
        </div>
      </div>
    );
  }

  // No signup yet — entry CTA
  return (
    <>
      <button onClick={() => { setSlots(nextWeekendSlots().slice(0, 2)); setShowPicker(true); }}
        style={{
          ...cardStyle, display: 'flex', alignItems: 'center', gap: 10,
          width: 'calc(100% - 32px)', textAlign: 'left', cursor: 'pointer', color: 'inherit',
        }}>
        <div style={{ fontSize: 22 }}>☕</div>
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: bh?.text || '#F2E8D0' }}>
            {fr ? `Café surprise · ${groupName || 'Hive'}` : `Coffee Roulette · ${groupName || 'Hive'}`}
          </p>
          <p style={{ margin: '3px 0 0', fontSize: 11, color: bh?.sub || '#8A7A5A' }}>
            {fr ? "Match auto avec un membre du groupe ce week-end." : 'Auto-match with a group member this weekend.'}
          </p>
        </div>
        <span style={{ fontSize: 11, color: '#C9A84C', fontWeight: 600 }}>
          {fr ? "M'inscrire" : 'Sign up'} →
        </span>
      </button>

      {showPicker && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(10,10,10,0.85)', zIndex: 9000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          fontFamily: 'Outfit',
        }} onClick={() => setShowPicker(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#161616', border: '1px solid rgba(201,168,76,0.3)',
            borderRadius: 12, padding: 18, maxWidth: 360, width: '100%',
          }}>
            <p style={{ margin: 0, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C9A84C' }}>
              {fr ? 'Café surprise' : 'Coffee Roulette'}
            </p>
            <h3 style={{ margin: '6px 0 12px', fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: '#F2E8D0', fontWeight: 400 }}>
              {fr ? 'Quels créneaux te conviennent ?' : 'Which slots work for you?'}
            </h3>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: '#888', lineHeight: 1.5 }}>
              {fr
                ? 'On apparie automatiquement avec quelqu\'un du même groupe Hive et un créneau commun.'
                : "We auto-match you with someone in the same hive group and a shared slot."}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              {nextWeekendSlots().map(s => {
                const checked = slots.includes(s);
                return (
                  <label key={s} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 10px',
                    background: checked ? 'rgba(201,168,76,0.1)' : '#0A0A0A',
                    border: `1px solid ${checked ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.08)'}`,
                    borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#F2E8D0',
                  }}>
                    <input type="checkbox" checked={checked} onChange={() => {
                      setSlots(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
                    }} style={{ accentColor: '#C9A84C' }}/>
                    {formatSlot(s, lang)}
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowPicker(false)} style={{
                flex: 1, padding: 10, background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6,
                color: '#888', fontSize: 12, cursor: 'pointer',
              }}>
                {fr ? 'Annuler' : 'Cancel'}
              </button>
              <button onClick={handleJoin} disabled={!slots.length || busy} style={{
                flex: 1, padding: 10, background: slots.length ? '#C9A84C' : 'transparent',
                border: 'none', borderRadius: 6,
                color: slots.length ? '#0A0A0A' : '#555', fontSize: 12, fontWeight: 700,
                cursor: slots.length && !busy ? 'pointer' : 'not-allowed',
              }}>
                {fr ? 'Confirmer' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
